import React, { useState, useEffect } from 'react';
import { 
  CheckIcon, 
  TrashIcon, 
  ExclamationTriangleIcon,
  InformationCircleIcon,
  ShoppingBagIcon,
  CreditCardIcon,
  StarIcon,
  MegaphoneIcon,
  CubeIcon,
  FunnelIcon,
  CheckCircleIcon
} from '@heroicons/react/24/outline';
import { formatDistanceToNow } from 'date-fns';
import { he } from 'date-fns/locale';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';

const NotificationsPage = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    type: '',
    is_read: '',
    priority: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });
  const [selectedNotifications, setSelectedNotifications] = useState(new Set());
  const [unreadCount, setUnreadCount] = useState(0);
  
  const { user, isAuthenticated } = useAuth();

  // Fetch notifications
  const fetchNotifications = async (page = 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: page.toString(),
        limit: pagination.limit.toString(),
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, value]) => value !== '')
        )
      });

      const response = await axios.get(`/api/notifications?${params}`);
      setNotifications(response.data.notifications);
      setPagination(response.data.pagination);
      setUnreadCount(response.data.unreadCount);
    } catch (error) {
      toast.error('שגיאה בטעינת ההתראות');
    } finally {
      setLoading(false);
    }
  };

  // Get notification icon
  const getNotificationIcon = (type, priority) => {
    const iconClass = `h-6 w-6 ${
      priority === 'urgent' ? 'text-red-500' :
      priority === 'high' ? 'text-orange-500' :
      priority === 'medium' ? 'text-blue-500' :
      'text-gray-500'
    }`;

    switch (type) {
      case 'order_created':
      case 'order_status_changed':
      case 'order_shipped':
      case 'order_delivered':
        return <ShoppingBagIcon className={iconClass} />;
      
      case 'payment_received':
      case 'payment_failed':
      case 'credit_limit_warning':
      case 'credit_limit_exceeded':
        return <CreditCardIcon className={iconClass} />;
      
      case 'inventory_low':
      case 'inventory_out':
        return <CubeIcon className={iconClass} />;
      
      case 'review_created':
        return <StarIcon className={iconClass} />;
      
      case 'promotion_created':
        return <MegaphoneIcon className={iconClass} />;
      
      case 'system_announcement':
      case 'account_update':
        return <InformationCircleIcon className={iconClass} />;
      
      default:
        return <InformationCircleIcon className={iconClass} />;
    }
  };

  // Format notification time
  const formatNotificationTime = (dateString) => {
    try {
      const date = new Date(dateString);
      return formatDistanceToNow(date, { 
        addSuffix: true, 
        locale: he 
      });
    } catch (error) {
      return 'זמן לא ידוע';
    }
  };

  // Mark notification as read
  const markAsRead = async (notificationId) => {
    try {
      await axios.patch(`/api/notifications/${notificationId}/read`);
      
      setNotifications(prev => 
        prev.map(notif => 
          notif.id === notificationId 
            ? { ...notif, is_read: true, read_at: new Date().toISOString() }
            : notif
        )
      );
      
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      toast.error('שגיאה בעדכון ההתראה');
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      await axios.patch('/api/notifications/mark-all-read');
      
      setNotifications(prev => 
        prev.map(notif => ({ 
          ...notif, 
          is_read: true, 
          read_at: new Date().toISOString() 
        }))
      );
      
      setUnreadCount(0);
      toast.success('כל ההתראות סומנו כנקראו');
    } catch (error) {
      toast.error('שגיאה בעדכון ההתראות');
    }
  };

  // Delete notification
  const deleteNotification = async (notificationId) => {
    try {
      await axios.delete(`/api/notifications/${notificationId}`);
      
      const deletedNotification = notifications.find(n => n.id === notificationId);
      setNotifications(prev => prev.filter(notif => notif.id !== notificationId));
      
      if (deletedNotification && !deletedNotification.is_read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
      
      toast.success('ההתראה נמחקה');
    } catch (error) {
      toast.error('שגיאה במחיקת ההתראה');
    }
  };

  // Delete read notifications
  const deleteReadNotifications = async () => {
    try {
      await axios.delete('/api/notifications/read');
      
      setNotifications(prev => prev.filter(notif => !notif.is_read));
      toast.success('ההתראות שנקראו נמחקו');
    } catch (error) {
      toast.error('שגיאה במחיקת ההתראות');
    }
  };

  // Handle notification click
  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
    
    if (notification.action_url) {
      window.location.href = notification.action_url;
    }
  };

  // Handle filter change
  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Apply filters
  const applyFilters = () => {
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchNotifications(1);
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      type: '',
      is_read: '',
      priority: ''
    });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Load notifications on mount and filter changes
  useEffect(() => {
    if (isAuthenticated) {
      fetchNotifications(pagination.page);
    }
  }, [isAuthenticated, pagination.page]);

  // Clear filters effect
  useEffect(() => {
    if (Object.values(filters).every(value => value === '')) {
      fetchNotifications(1);
    }
  }, [filters]);

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">נדרשת התחברות</h2>
          <p className="text-gray-600">יש להתחבר כדי לצפות בהתראות</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">התראות</h1>
          <div className="flex items-center justify-between">
            <p className="text-gray-600">
              {unreadCount > 0 ? `יש לך ${unreadCount} התראות שלא נקראו` : 'כל ההתראות נקראו'}
            </p>
            
            <div className="flex items-center gap-3">
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                >
                  <CheckCircleIcon className="h-4 w-4 ml-2" />
                  סמן הכל כנקרא
                </button>
              )}
              
              <button
                onClick={deleteReadNotifications}
                className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                <TrashIcon className="h-4 w-4 ml-2" />
                מחק נקראו
              </button>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <FunnelIcon className="h-5 w-5 ml-2" />
              מסננים
            </h3>
            
            <button
              onClick={clearFilters}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              נקה מסננים
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Type Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                סוג התראה
              </label>
              <select
                value={filters.type}
                onChange={(e) => handleFilterChange('type', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">כל הסוגים</option>
                <option value="order_created">הזמנה חדשה</option>
                <option value="order_status_changed">שינוי סטטוס הזמנה</option>
                <option value="payment_received">תשלום התקבל</option>
                <option value="inventory_low">מלאי נמוך</option>
                <option value="review_created">ביקורת חדשה</option>
                <option value="promotion_created">מבצע חדש</option>
                <option value="system_announcement">הודעת מערכת</option>
              </select>
            </div>

            {/* Read Status Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                סטטוס קריאה
              </label>
              <select
                value={filters.is_read}
                onChange={(e) => handleFilterChange('is_read', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">הכל</option>
                <option value="false">לא נקרא</option>
                <option value="true">נקרא</option>
              </select>
            </div>

            {/* Priority Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                עדיפות
              </label>
              <select
                value={filters.priority}
                onChange={(e) => handleFilterChange('priority', e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="">כל העדיפויות</option>
                <option value="urgent">דחוף</option>
                <option value="high">גבוה</option>
                <option value="medium">בינוני</option>
                <option value="low">נמוך</option>
              </select>
            </div>
          </div>
          
          <div className="mt-4">
            <button
              onClick={applyFilters}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 font-medium"
            >
              החל מסננים
            </button>
          </div>
        </div>

        {/* Notifications List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <span className="mr-3 text-gray-600">טוען התראות...</span>
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <InformationCircleIcon className="h-16 w-16 text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">אין התראות</h3>
              <p className="text-gray-500">לא נמצאו התראות התואמות לקריטריונים</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`p-6 hover:bg-gray-50 cursor-pointer transition-colors ${
                    !notification.is_read ? 'bg-blue-50 border-r-4 border-r-blue-500' : ''
                  }`}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="flex items-start space-x-4 space-x-reverse">
                    {/* Icon */}
                    <div className="flex-shrink-0">
                      {getNotificationIcon(notification.type, notification.priority)}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h3 className={`text-lg font-medium ${
                              !notification.is_read ? 'text-gray-900' : 'text-gray-600'
                            }`}>
                              {notification.title}
                            </h3>
                            
                            {notification.is_important && (
                              <ExclamationTriangleIcon className="h-5 w-5 text-amber-500" />
                            )}
                            
                            {!notification.is_read && (
                              <div className="w-2 h-2 bg-primary-600 rounded-full"></div>
                            )}
                          </div>
                          
                          <p className={`text-base mb-3 ${
                            !notification.is_read ? 'text-gray-700' : 'text-gray-500'
                          }`}>
                            {notification.message}
                          </p>
                          
                          <div className="flex items-center gap-4 text-sm text-gray-400">
                            <span>{formatNotificationTime(notification.sent_at)}</span>
                            <span className="capitalize">{notification.priority}</span>
                            {notification.read_at && (
                              <span>נקרא {formatNotificationTime(notification.read_at)}</span>
                            )}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="flex items-center space-x-2 space-x-reverse mr-4">
                          {!notification.is_read && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                markAsRead(notification.id);
                              }}
                              className="p-2 text-gray-400 hover:text-green-600 transition-colors rounded-lg hover:bg-green-50"
                              title="סמן כנקרא"
                            >
                              <CheckIcon className="h-5 w-5" />
                            </button>
                          )}
                          
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteNotification(notification.id);
                            }}
                            className="p-2 text-gray-400 hover:text-red-600 transition-colors rounded-lg hover:bg-red-50"
                            title="מחק התראה"
                          >
                            <TrashIcon className="h-5 w-5" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Pagination */}
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between mt-6">
            <div className="text-sm text-gray-700">
              מציג {((pagination.page - 1) * pagination.limit) + 1} עד {Math.min(pagination.page * pagination.limit, pagination.total)} מתוך {pagination.total} התראות
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                הקודם
              </button>
              
              <span className="px-4 py-2 text-sm font-medium text-gray-700">
                עמוד {pagination.page} מתוך {pagination.pages}
              </span>
              
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page === pagination.pages}
                className="px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                הבא
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default NotificationsPage;
